import { useRef, useState } from "react"
import cc from "classcat"
import H2 from "components/atoms/H2"
import useTranslations from "hooks/useTranslations"
import { MockupImages } from "./images"

/**
 * Deck slide 18 lays the product out as Prevention / Reporting / Intervention /
 * Dashboard, which is the same four things in the same order as the tabs below.
 * Positional rather than keyed by name, because the tab names come from
 * translations and would drift.
 */
const MOCKUP_ORDER = [
  // The two phone shots are a hand reaching in from off-frame, and the source
  // images stop partway up the forearm. Floated inside the card's padding that
  // cut edge is visible and reads as a mistake, so these bleed into the card's
  // bottom-right corner and let it clip the wrist instead.
  { Image: MockupImages.learn, bleed: true },
  { Image: MockupImages.get, bleed: true },
  // The laptops are whole objects with their own edges — centred, they look
  // deliberate; bled, they would look cropped.
  { Image: MockupImages.give, bleed: false },
  { Image: MockupImages.impact, bleed: false },
]

/**
 * "We want to make your school the safest school in the UK" — four tabs on
 * desktop, stacked on mobile.
 *
 * Essi asked for a carousel. A tabbed panel is the same idea without the two
 * things auto-advancing carousels cost you: copy the reader cannot finish, and
 * content search engines and screen readers reach only one slide of. Every
 * panel is in the DOM; only the visible one is shown.
 */
export default function UkCarousel() {
  const {
    ukSchoolsCarouselTitle,
    ukSchoolsCarouselLead,
    ukSchoolsCarouselNames,
    ukSchoolsCarouselTexts,
  } = useTranslations()

  const names = ukSchoolsCarouselNames || []
  const texts = ukSchoolsCarouselTexts || []
  const [active, setActive] = useState(0)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  /**
   * `role="tablist"` promises the WAI-ARIA tabs pattern, so it has to keep it:
   * arrows move between tabs and wrap at the ends, Home and End jump to either
   * end. Paired with the roving tabindex below, which puts only the selected
   * tab in the page's tab order — Tab then moves past the whole group rather
   * than stepping through four buttons.
   */
  const onTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const last = names.length - 1
    let next: number | null = null
    if (event.key === "ArrowRight") next = index === last ? 0 : index + 1
    else if (event.key === "ArrowLeft") next = index === 0 ? last : index - 1
    else if (event.key === "Home") next = 0
    else if (event.key === "End") next = last
    if (next === null) return

    event.preventDefault()
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  if (!names.length) return null

  return (
    <div className="bg-kkm-green py-16 lg:py-24">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <H2
          size="lg"
          className="m-0 mb-3 whitespace-pre-line text-left font-extrabold tracking-tight"
          theme="green"
        >
          {ukSchoolsCarouselTitle}
        </H2>
        {/* Jasmina: "We want to:" read straight into each tab name — "We want
            to Impact Value Dashboard". The lead is empty now; the headline
            already carries the sentence. Kept as a key so it can come back. */}
        {ukSchoolsCarouselLead ? (
          <p className="m-0 mb-10 font-display text-lg text-zinc-800 lg:text-xl">
            {ukSchoolsCarouselLead}
          </p>
        ) : null}

        <div
          role="tablist"
          aria-label={ukSchoolsCarouselTitle?.replace(/\n/g, " ")}
          className="mb-8 mt-10 flex flex-wrap gap-3"
        >
          {names.map((name, index) => (
            <button
              key={name}
              type="button"
              role="tab"
              ref={(element) => {
                tabRefs.current[index] = element
              }}
              id={`uk-carousel-tab-${index}`}
              aria-selected={index === active}
              aria-controls={`uk-carousel-panel-${index}`}
              tabIndex={index === active ? 0 : -1}
              onClick={() => setActive(index)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
              className={cc([
                "rounded-full px-5 py-3 font-display text-base lg:text-lg",
                "transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.96]",
                // Offset in the band colour: keyboard focus now lands on the
                // selected tab, which is itself near-black, and an unoffset
                // zinc-900 ring against it just reads as a fatter pill.
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 focus-visible:ring-offset-kkm-green",
                index === active
                  ? "bg-zinc-900 text-zinc-50"
                  : "bg-white/70 text-zinc-800 hover:bg-white",
              ])}
            >
              {name}
            </button>
          ))}
        </div>

        {names.map((name, index) => {
          const { Image: Mockup, bleed } = MOCKUP_ORDER[index] || {}
          return (
            <div
              key={name}
              role="tabpanel"
              id={`uk-carousel-panel-${index}`}
              aria-labelledby={`uk-carousel-tab-${index}`}
              hidden={index !== active}
              // `overflow-hidden` is what makes the bleed work: it clips the
              // mockup to the card's rounded corner instead of letting it spill.
              className="max-w-5xl overflow-hidden rounded-2xl bg-white p-6 lg:p-10"
            >
              <div className="grid grid-cols-1 items-center gap-x-10 gap-y-8 lg:grid-cols-[1fr_18rem]">
                <div>
                  <h3 className="m-0 mb-4 font-display text-2xl font-bold text-zinc-900">
                    {name}
                  </h3>
                  <p className="m-0 text-base leading-relaxed text-zinc-700 lg:text-lg">
                    {texts[index] || ""}
                  </p>
                </div>
                {Mockup ? (
                  <div
                    className={cc([
                      "mx-auto w-full max-w-[18rem]",
                      // Negative margins cancel the card's own padding on the
                      // two edges the image should run past. Desktop only —
                      // stacked on mobile the image is centred under the text,
                      // where a right-hand bleed would just look lopsided.
                      // No `lg:mr-0` here — it and `lg:-mr-10` both set
                      // margin-right, and the zero wins, which silently
                      // reinstates the padding this is meant to cancel.
                      bleed
                        ? "lg:-mb-10 lg:-mr-10 lg:ml-auto lg:max-w-none lg:self-end"
                        : "lg:self-center",
                    ])}
                  >
                    {/* GatsbyImage puts this className on its own wrapper,
                        which already carries .gatsby-image-wrapper-constrained
                        — that sets a max-width from the image's intrinsic size
                        and display:inline-block. Both beat plain Tailwind
                        utilities, so the sizing lives on the parent above and
                        Gatsby's cap is explicitly cleared here; otherwise the
                        image stops a few pixels short of the card edge. */}
                    <Mockup
                      alt={`${name} shown in the SomeBuddy product`}
                      className="!block w-full !max-w-none"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          )
        })}
      </section>
    </div>
  )
}
